import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, vModelText as _vModelText, normalizeClass as _normalizeClass, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers, Transition as _Transition, withCtx as _withCtx, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "bg-card rounded-lg shadow-2xl border border-border"
}
const _hoisted_2 = { class: "flex items-center justify-between p-6 border-b border-border" }
const _hoisted_3 = { class: "text-2xl font-bold text-foreground" }
const _hoisted_4 = { class: "p-6 space-y-4" }
const _hoisted_5 = {
  key: 0,
  class: "text-red-500 text-sm mt-1"
}
const _hoisted_6 = {
  key: 0,
  class: "text-red-500 text-sm mt-1"
}
const _hoisted_7 = ["disabled"]
const _hoisted_8 = {
  key: 0,
  class: "inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent"
}
const _hoisted_9 = { class: "text-center text-sm text-muted-foreground" }
const _hoisted_10 = {
  key: 0,
  class: "text-red-500 text-sm mt-1"
}
const _hoisted_11 = {
  key: 0,
  class: "text-red-500 text-sm mt-1"
}
const _hoisted_12 = {
  key: 0,
  class: "text-red-500 text-sm mt-1"
}
const _hoisted_13 = {
  key: 0,
  class: "text-red-500 text-sm mt-1"
}
const _hoisted_14 = {
  key: 0,
  class: "text-red-500 text-sm mt-1"
}
const _hoisted_15 = {
  key: 0,
  class: "text-red-500 text-sm mt-1"
}
const _hoisted_16 = ["disabled"]
const _hoisted_17 = {
  key: 0,
  class: "inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent"
}
const _hoisted_18 = { class: "text-center text-sm text-muted-foreground" }

import { ref, reactive } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useCartStore } from  '@/stores/cartStore';
import { useCheckoutStore} from '@/stores/checkoutStore';
import { useToast } from '@/composables/useToast';
import type { IUserRequest } from '@/data/_autogen/IValveVista';
import {useRouter} from 'vue-router';

interface Props {
  isOpen: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AuthModal',
  props: {
    isOpen: { type: Boolean }
  },
  emits: ["close", "loginSuccess", "registerSuccess"],
  setup(__props: any, { emit: __emit }) {



const emit = __emit;

const authStore = useAuthStore();
const cartStore =  useCartStore();
const checkoutStore = useCheckoutStore();

const router = useRouter();

const { success, error: showError } = useToast();

const mode = ref<'login' | 'register'>('login');
const isSubmitting = ref(false);

// Login form state
const loginForm = reactive({
  emailAddress: '',
  password: '',
});

// Register form state
const registerForm = reactive({
  firstName: '',
  lastName: '',
  username: '',
  emailAddress: '',
  password: '',
  confirmPassword: '',
});

// Form errors
const formErrors = reactive({
  username: '',
  emailAddress: '',
  password: '',
  firstName: '',
  lastName: '',
  confirmPassword: '',
});

/**
 * Validate login form
 */
const validateLoginForm = (): boolean => {
  formErrors.emailAddress = '';
  formErrors.password = '';

  let isValid = true;

  if (!loginForm.emailAddress) {
    formErrors.emailAddress = 'Email or username required';
    isValid = false;
  }

  if (!loginForm.password) {
    formErrors.password = 'Password required';
    isValid = false;
  }

  return isValid;
};

/**
 * Validate register form
 */
const validateRegisterForm = (): boolean => {
  formErrors.firstName = '';
  formErrors.lastName = '';
  formErrors.username = '';
  formErrors.emailAddress = '';
  formErrors.password = '';
  formErrors.confirmPassword = '';

  let isValid = true;

  if (!registerForm.firstName) {
    formErrors.firstName = 'First name required';
    isValid = false;
  }

  if (!registerForm.lastName) {
    formErrors.lastName = 'Last name required';
    isValid = false;
  }

  if(!registerForm.username) {
    formErrors.username = 'Username required';
    isValid = false;
  }

  if (!registerForm.emailAddress) {
    formErrors.emailAddress = 'Email required';
    isValid = false;
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(registerForm.emailAddress)) {
    formErrors.emailAddress = 'Invalid email format';
    isValid = false;
  }

  const strongPasswordRegex = new RegExp(
    '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[!@#$%^&*()_+={}\\[\\]:;"\'<>,.?/|\\~-])[A-Za-z\\d!@#$%^&*()_+={}\\[\\]:;"\'<>,.?/|\\~-]{8,}$'
  );

  if (!registerForm.password) {
    formErrors.password = 'Password required';
    isValid = false;
  } else if (
    registerForm.password.length < 8 || !strongPasswordRegex.test(registerForm.password)
    /*|| !/[A-Z]/.test(registerForm.password) ||
    !/[a-z]/.test(registerForm.password) ||
    !/[0-9]/.test(registerForm.password)*/
  ) {
    formErrors.password =
      'Password must be at least 8 characters with uppercase, lowercase, number and special character';
    isValid = false;
  }

  if (registerForm.password !== registerForm.confirmPassword) {
    formErrors.confirmPassword = 'Passwords do not match';
    isValid = false;
  }

  return isValid;
};

/**
 * Handle login form submission
 */
const handleLogin = async () => {
  if (!validateLoginForm()) {
    return;
  }

  isSubmitting.value = true;

  try {
    const userRequest: IUserRequest = {
      emailAddress: loginForm.emailAddress.includes('@')? loginForm.emailAddress : undefined,
      userName: loginForm.emailAddress, // Can be either email or username
      password: loginForm.password,
    };

    const isGuest = authStore.isGuest;
    await authStore.login(userRequest);

    if(isGuest)
    {
      if(checkoutStore.checkoutSession)
        checkoutStore.resetCheckout();
      if(cartStore.cartId != null)
        cartStore.resetCart();
    }

    success('Login successful!');
    handleClose();
    emit('loginSuccess');
    await router.push('/');
  } catch (err: any) {
    const errorMessage =
      err.response?.data?.error || err.message || 'Login failed. Please try again.';
    showError(errorMessage);
  } finally {
    isSubmitting.value = false;
  }
};

/**
 * Handle register form submission
 */
const handleRegister = async () => {
  if (!validateRegisterForm()) {
    return;
  }

  isSubmitting.value = true;

  try {
    const userRequest: IUserRequest = {
      userName: registerForm.username.trim(),
      firstName: registerForm.firstName,
      lastName: registerForm.lastName,
      emailAddress: registerForm.emailAddress,
      password: registerForm.password.trim(),
    };

    const isGuest = authStore.isGuest;
    await authStore.register(userRequest);

    if(isGuest)
    {
      if(checkoutStore.checkoutSession)
        checkoutStore.resetCheckout();
      if(cartStore.cartId != null)
        cartStore.resetCart();
    }

    success('Registration successful!');
    handleClose();
    emit('registerSuccess');
    await router.push('/');
  } catch (err: any) {
    const errorMessage =
      err.response?.data?.error || err.message || 'Registration failed. Please try again.';
    showError(errorMessage);
  } finally {
    isSubmitting.value = false;

  }
};

const handleClose = () => {
  // Reset form
  loginForm.emailAddress = '';
  loginForm.password = '';
  registerForm.firstName = '';
  registerForm.lastName = '';
  registerForm.username = '';
  registerForm.emailAddress = '';
  registerForm.password = '';
  registerForm.confirmPassword = '';
  mode.value = 'login';

  emit('close');

};

const handleBackdropClick = () => {
  handleClose();
};

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    _createVNode(_Transition, { name: "fade" }, {
      default: _withCtx(() => [
        (__props.isOpen)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4",
              onClick: handleBackdropClick
            }, [
              _createElementVNode("div", {
                class: "w-full max-w-md",
                onClick: _cache[10] || (_cache[10] = _withModifiers(() => {}, ["stop"]))
              }, [
                _createVNode(_Transition, { name: "scale-up" }, {
                  default: _withCtx(() => [
                    (__props.isOpen)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                          _createElementVNode("div", _hoisted_2, [
                            _createElementVNode("h2", _hoisted_3, _toDisplayString(mode.value === 'login' ? 'Login' : 'Register'), 1),
                            _createElementVNode("button", {
                              class: "p-2 text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-primary/10",
                              "aria-label": "Close modal",
                              onClick: handleClose
                            }, [
                              _createVNode(_component_font_awesome_icon, {
                                icon: ['fal', 'times'],
                                class: "text-xl"
                              })
                            ])
                          ]),
                          _createElementVNode("div", _hoisted_4, [
                            (mode.value === 'login')
                              ? (_openBlock(), _createElementBlock("form", {
                                  key: 0,
                                  class: "space-y-4",
                                  onSubmit: _withModifiers(handleLogin, ["prevent"])
                                }, [
                                  _createElementVNode("div", null, [
                                    _cache[11] || (_cache[11] = _createElementVNode("label", {
                                      for: "login-email",
                                      class: "block text-sm font-medium text-foreground mb-2"
                                    }, " Email or Username ", -1)),
                                    _withDirectives(_createElementVNode("input", {
                                      id: "login-email",
                                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((loginForm.emailAddress) = $event)),
                                      type: "text",
                                      placeholder: "Enter email or username",
                                      class: _normalizeClass(["w-full px-4 py-2 rounded-lg border border-input bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary", { 'border-red-500': formErrors.emailAddress }])
                                    }, null, 2), [
                                      [_vModelText, loginForm.emailAddress]
                                    ]),
                                    (formErrors.emailAddress)
                                      ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(formErrors.emailAddress), 1))
                                      : _createCommentVNode("", true)
                                  ]),
                                  _createElementVNode("div", null, [
                                    _cache[12] || (_cache[12] = _createElementVNode("label", {
                                      for: "login-password",
                                      class: "block text-sm font-medium text-foreground mb-2"
                                    }, " Password ", -1)),
                                    _withDirectives(_createElementVNode("input", {
                                      id: "login-password",
                                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((loginForm.password) = $event)),
                                      type: "password",
                                      placeholder: "Enter password",
                                      class: _normalizeClass(["w-full px-4 py-2 rounded-lg border border-input bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary", { 'border-red-500': formErrors.password }])
                                    }, null, 2), [
                                      [_vModelText, loginForm.password]
                                    ]),
                                    (formErrors.password)
                                      ? (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(formErrors.password), 1))
                                      : _createCommentVNode("", true)
                                  ]),
                                  _createElementVNode("button", {
                                    type: "submit",
                                    disabled: isSubmitting.value,
                                    class: "w-full btn-primary rounded-lg px-4 py-3 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                  }, [
                                    (isSubmitting.value)
                                      ? (_openBlock(), _createElementBlock("span", _hoisted_8))
                                      : _createCommentVNode("", true),
                                    _createTextVNode(" " + _toDisplayString(isSubmitting.value ? 'Logging in...' : 'Login'), 1)
                                  ], 8, _hoisted_7),
                                  _createElementVNode("p", _hoisted_9, [
                                    _cache[13] || (_cache[13] = _createTextVNode(" Don't have an account? ", -1)),
                                    _createElementVNode("button", {
                                      type: "button",
                                      class: "text-primary hover:underline font-semibold",
                                      onClick: _cache[2] || (_cache[2] = ($event: any) => (mode.value = 'register'))
                                    }, " Register ")
                                  ])
                                ], 32))
                              : _createCommentVNode("", true),
                            (mode.value === 'register')
                              ? (_openBlock(), _createElementBlock("form", {
                                  key: 1,
                                  class: "space-y-4",
                                  onSubmit: _withModifiers(handleRegister, ["prevent"])
                                }, [
                                  _createElementVNode("div", null, [
                                    _cache[14] || (_cache[14] = _createElementVNode("label", {
                                      for: "register-user-name",
                                      class: "block text-sm font-medium text-foreground mb-2"
                                    }, " User Name ", -1)),
                                    _withDirectives(_createElementVNode("input", {
                                      id: "register-user-name",
                                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((registerForm.username) = $event)),
                                      type: "text",
                                      placeholder: "Enter user name",
                                      class: _normalizeClass(["w-full px-4 py-2 rounded-lg border border-input bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary", { 'border-red-500': formErrors.username }])
                                    }, null, 2), [
                                      [_vModelText, registerForm.username]
                                    ]),
                                    (formErrors.username)
                                      ? (_openBlock(), _createElementBlock("p", _hoisted_10, _toDisplayString(formErrors.username), 1))
                                      : _createCommentVNode("", true)
                                  ]),
                                  _createElementVNode("div", null, [
                                    _cache[15] || (_cache[15] = _createElementVNode("label", {
                                      for: "register-first-name",
                                      class: "block text-sm font-medium text-foreground mb-2"
                                    }, " First Name ", -1)),
                                    _withDirectives(_createElementVNode("input", {
                                      id: "register-first-name",
                                      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((registerForm.firstName) = $event)),
                                      type: "text",
                                      placeholder: "Enter first name",
                                      class: _normalizeClass(["w-full px-4 py-2 rounded-lg border border-input bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary", { 'border-red-500': formErrors.firstName }])
                                    }, null, 2), [
                                      [_vModelText, registerForm.firstName]
                                    ]),
                                    (formErrors.firstName)
                                      ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(formErrors.firstName), 1))
                                      : _createCommentVNode("", true)
                                  ]),
                                  _createElementVNode("div", null, [
                                    _cache[16] || (_cache[16] = _createElementVNode("label", {
                                      for: "register-last-name",
                                      class: "block text-sm font-medium text-foreground mb-2"
                                    }, " Last Name ", -1)),
                                    _withDirectives(_createElementVNode("input", {
                                      id: "register-last-name",
                                      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((registerForm.lastName) = $event)),
                                      type: "text",
                                      placeholder: "Enter last name",
                                      class: _normalizeClass(["w-full px-4 py-2 rounded-lg border border-input bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary", { 'border-red-500': formErrors.lastName }])
                                    }, null, 2), [
                                      [_vModelText, registerForm.lastName]
                                    ]),
                                    (formErrors.lastName)
                                      ? (_openBlock(), _createElementBlock("p", _hoisted_12, _toDisplayString(formErrors.lastName), 1))
                                      : _createCommentVNode("", true)
                                  ]),
                                  _createElementVNode("div", null, [
                                    _cache[17] || (_cache[17] = _createElementVNode("label", {
                                      for: "register-email",
                                      class: "block text-sm font-medium text-foreground mb-2"
                                    }, " Email Address ", -1)),
                                    _withDirectives(_createElementVNode("input", {
                                      id: "register-email",
                                      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((registerForm.emailAddress) = $event)),
                                      type: "email",
                                      placeholder: "Enter email address",
                                      class: _normalizeClass(["w-full px-4 py-2 rounded-lg border border-input bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary", { 'border-red-500': formErrors.emailAddress }])
                                    }, null, 2), [
                                      [_vModelText, registerForm.emailAddress]
                                    ]),
                                    (formErrors.emailAddress)
                                      ? (_openBlock(), _createElementBlock("p", _hoisted_13, _toDisplayString(formErrors.emailAddress), 1))
                                      : _createCommentVNode("", true)
                                  ]),
                                  _createElementVNode("div", null, [
                                    _cache[18] || (_cache[18] = _createElementVNode("label", {
                                      for: "register-password",
                                      class: "block text-sm font-medium text-foreground mb-2"
                                    }, [
                                      _createTextVNode(" Password "),
                                      _createElementVNode("span", { class: "text-xs text-muted-foreground" }, "(min 8 chars, uppercase, lowercase, number)")
                                    ], -1)),
                                    _withDirectives(_createElementVNode("input", {
                                      id: "register-password",
                                      "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((registerForm.password) = $event)),
                                      type: "password",
                                      placeholder: "Enter password",
                                      class: _normalizeClass(["w-full px-4 py-2 rounded-lg border border-input bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary", { 'border-red-500': formErrors.password }])
                                    }, null, 2), [
                                      [_vModelText, registerForm.password]
                                    ]),
                                    (formErrors.password)
                                      ? (_openBlock(), _createElementBlock("p", _hoisted_14, _toDisplayString(formErrors.password), 1))
                                      : _createCommentVNode("", true)
                                  ]),
                                  _createElementVNode("div", null, [
                                    _cache[19] || (_cache[19] = _createElementVNode("label", {
                                      for: "register-confirm-password",
                                      class: "block text-sm font-medium text-foreground mb-2"
                                    }, " Confirm Password ", -1)),
                                    _withDirectives(_createElementVNode("input", {
                                      id: "register-confirm-password",
                                      "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((registerForm.confirmPassword) = $event)),
                                      type: "password",
                                      placeholder: "Confirm password",
                                      class: _normalizeClass(["w-full px-4 py-2 rounded-lg border border-input bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary", { 'border-red-500': formErrors.confirmPassword }])
                                    }, null, 2), [
                                      [_vModelText, registerForm.confirmPassword]
                                    ]),
                                    (formErrors.confirmPassword)
                                      ? (_openBlock(), _createElementBlock("p", _hoisted_15, _toDisplayString(formErrors.confirmPassword), 1))
                                      : _createCommentVNode("", true)
                                  ]),
                                  _createElementVNode("button", {
                                    type: "submit",
                                    disabled: isSubmitting.value,
                                    class: "w-full btn-primary rounded-lg px-4 py-3 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                  }, [
                                    (isSubmitting.value)
                                      ? (_openBlock(), _createElementBlock("span", _hoisted_17))
                                      : _createCommentVNode("", true),
                                    _createTextVNode(" " + _toDisplayString(isSubmitting.value ? 'Creating account...' : 'Register'), 1)
                                  ], 8, _hoisted_16),
                                  _createElementVNode("p", _hoisted_18, [
                                    _cache[20] || (_cache[20] = _createTextVNode(" Already have an account? ", -1)),
                                    _createElementVNode("button", {
                                      type: "button",
                                      class: "text-primary hover:underline font-semibold",
                                      onClick: _cache[9] || (_cache[9] = ($event: any) => (mode.value = 'login'))
                                    }, " Login ")
                                  ])
                                ], 32))
                              : _createCommentVNode("", true)
                          ])
                        ]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                })
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

})